import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import AddFamilyModal from "./components/AddFamilyModal";
import BiosimilarConfigFooter from "./components/BiosimilarConfigFooter";
import BiosimilarConfigHeader from "./components/BiosimilarConfigHeader";
import BiosimilarFamilyCard from "./components/BiosimilarFamilyCard";
import { useBiosimilarConfigEditor } from "./hooks/useBiosimilarConfigEditor";
import { countDoseConversionFamilies } from "./utils/biosimilarConfigHelpers";

/**
 * Drug family configuration page for CMM biosimilar families.
 * Loads Firestore data on land and supports inline editing with save.
 *
 * @returns Biosimilar / drug family configuration layout
 */
const BiosimilarConfig = () => {
  const editor = useBiosimilarConfigEditor();
  const families = editor.config?.families ?? [];
  const doseConversionCount = countDoseConversionFamilies(families);

  return (
    <div className="biosimilar-config h-full w-full bg-primaryGray-16 p-4">
      <div className="biosimilar-config__inner-container flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        {editor.loading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderMessage message="Loading drug family configuration…" />
          </div>
        ) : editor.error ? (
          <div className="text-small text-primaryGray-4">
            Failed to load drug family configuration. {editor.error.message}
          </div>
        ) : (
          <>
            <BiosimilarConfigHeader
              filterQuery={editor.filterQuery}
              onFilterChange={editor.setFilterQuery}
              filteredCount={editor.filteredFamilies.length}
              totalCount={families.length}
              enabled={editor.config?.enabled !== false}
              onEnabledChange={editor.handleEnabledChange}
              saving={editor.saving}
            />

            <div className="biosimilar-config__content min-h-0 flex-1 overflow-y-auto">
              {editor.config == null ? (
                <div className="text-small text-primaryGray-4">
                  No configuration document was found at
                  api_config/pharmacy/cmm-biosimilar-families/v1.
                </div>
              ) : editor.filteredFamilies.length === 0 ? (
                <div className="text-small text-primaryGray-4">
                  No families match your filter.
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {editor.filteredFamilyIndexes.map((familyIndex) => {
                    const family = families[familyIndex];
                    if (!family) {
                      return null;
                    }
                    return (
                      <BiosimilarFamilyCard
                        key={`family-${familyIndex}`}
                        familyIndex={familyIndex}
                        family={family}
                        onUpdate={(patch) =>
                          editor.updateFamily(familyIndex, patch)
                        }
                        onDelete={() => editor.deleteFamily(familyIndex)}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <BiosimilarConfigFooter
              familyCount={families.length}
              doseConversionCount={doseConversionCount}
              lastSavedMeta={editor.lastSavedMeta}
              isDirty={editor.isDirty}
              saving={editor.saving}
              onSave={() => {
                void editor.saveChanges();
              }}
              onReset={editor.discardChanges}
            />
          </>
        )}
      </div>
      <AddFamilyModal
        hasFamilyName={editor.hasFamilyName}
        onConfirm={editor.addFamily}
      />
    </div>
  );
};

export default BiosimilarConfig;
